/**
 * Il piano di un esercizio di tiro, letto in un posto solo.
 *
 * Qui stanno le regole che prima erano sparse fra tre form e che, sparse,
 * dicevano cose diverse. Tre in particolare:
 *
 *  - **Le serie sono una lista, il numero è la sua lunghezza.** C'erano due
 *    campi — `series_planned` (un numero) e `series[]` (le righe con la
 *    distanza) — e il coach si chiedeva quale dei due servisse. Servono
 *    insieme: il numero dice quante, la riga dice *cosa cambia* in quella
 *    serie, e di norma non cambia niente. Si tengono allineati qui.
 *  - **`rounds_planned` sono i colpi per serie**, non il totale. È la lettura
 *    di chi allestisce («questa serie è da 5 colpi, la ripeto 4 volte») ed è
 *    anche quella che lo stage designer conta: una disposizione si spara una
 *    volta per serie. Il totale — le munizioni da portare — si deriva.
 *  - **I colpi dichiarati e i bersagli devono dire la stessa cosa.** Se il
 *    coach scrive «5 colpi» su una sagoma che ne conta 2, il tiratore non
 *    riesce a registrarne 5: la scheda lo ferma a 2. Lo scarto va mostrato
 *    e, quando si può, chiuso con un gesto (`applyRoundsToTargets`).
 *
 * Puro, senza React: lo usano il form del coach, la libreria drill, il
 * designer, il form punteggi e la vista atleta — ed è testato.
 *
 * Spec: docs/shooting/15-seconda-sessione-federico.md
 */
import { defaultHitsRequired, requiredShots } from "./stageElements";
import { isShootingExerciseIncomplete } from "../../lib/programBuilderUtils";

// ── Serie ──────────────────────────────────────────────────────────────────

/** Le righe di serie previste, sempre come lista. */
export const plannedSeriesList = (shooting) =>
	Array.isArray(shooting?.series) ? shooting.series : [];

/**
 * Quante serie prevede l'esercizio.
 *
 * La lista vince sul numero: se il coach ha scritto le righe, sono quelle.
 * Il numero da solo copre i drill salvati prima delle righe e quelli che
 * non hanno niente da dire sulla singola serie.
 */
export const plannedSeriesCount = (shooting) => {
	const list = plannedSeriesList(shooting);
	if (list.length > 0) return list.length;
	const raw = shooting?.series_planned ?? shooting?.strings_planned;
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/**
 * Porta la lista delle serie a `count` righe senza perdere quelle compilate.
 *
 * Si aggiungono righe vuote in coda e si tolgono dalla coda: cambiare 5 in 3
 * non deve cancellare la distanza scritta sulla prima.
 */
export const resizePlannedSeries = (list, count) => {
	const parsed = Number(count);
	const wanted =
		Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
	const current = Array.isArray(list) ? list : [];
	if (wanted <= current.length) return current.slice(0, wanted);
	return [
		...current,
		...Array.from({ length: wanted - current.length }, () => ({
			distance_m: null,
			label: "",
		})),
	];
};

/** Una riga di serie prevista ha qualcosa scritto sopra? */
export const seriesPlanHasDetail = (serie) =>
	!!serie &&
	((serie.distance_m !== null &&
		serie.distance_m !== undefined &&
		serie.distance_m !== "") ||
		String(serie.label || "").trim() !== "");

/**
 * Il titolo di una scheda di serie: l'etichetta, o la distanza, o niente.
 *
 * Prima la riga registrata, poi quella prevista: se il tiratore ha scritto
 * «in ginocchio» su una serie che il coach aveva previsto a 10 m, vince ciò
 * che è stato fatto davvero.
 */
export const seriesHeading = (serie, planned) => {
	const distance = (value) =>
		value !== null && value !== undefined && value !== ""
			? `${value} m`
			: null;
	return (
		(serie?.label ? String(serie.label) : null) ||
		distance(serie?.distance_m) ||
		(planned?.label ? String(planned.label) : null) ||
		distance(planned?.distance_m) ||
		null
	);
};

// ── Colpi ──────────────────────────────────────────────────────────────────

/**
 * Colpi per serie: quelli dichiarati dal coach, altrimenti quelli che la
 * disposizione dei bersagli richiede.
 */
export const roundsPerSeries = (shooting) => {
	const declared = Number(shooting?.rounds_planned);
	if (Number.isFinite(declared) && declared > 0) return declared;
	return requiredShots(shooting?.targets) || 0;
};

/** Munizioni da portare: colpi per serie × serie (almeno una). */
export const totalRoundsPlanned = (shooting) =>
	roundsPerSeries(shooting) * Math.max(1, plannedSeriesCount(shooting));

/**
 * Lo scarto fra i colpi dichiarati e quelli che i bersagli ammettono.
 *
 * `null` quando non c'è niente da confrontare (nessun bersaglio, nessun
 * numero dichiarato) o quando tornano. Altrimenti i due numeri, per
 * mostrarli — e per decidere se si può chiudere lo scarto con un gesto.
 */
export const roundsMismatch = (shooting) => {
	const targets = Array.isArray(shooting?.targets) ? shooting.targets : [];
	const declared = Number(shooting?.rounds_planned);
	if (!targets.length || !Number.isFinite(declared) || declared <= 0) return null;
	const fromTargets = requiredShots(targets);
	if (!fromTargets || fromTargets === declared) return null;
	return { declared, fromTargets };
};

/**
 * Distribuisce i colpi dichiarati sui bersagli che si sparano.
 *
 * È il caso del lento mirato: una sagoma sola e «5 colpi» scritto accanto.
 * Senza questo gesto il coach doveva sapere che esiste un campo `colpi` sul
 * singolo bersaglio, e il tiratore trovava la scheda ferma a 2.
 *
 * Restituisce `null` quando i colpi non si dividono in parti uguali fra i
 * bersagli: lì la scelta è del coach, bersaglio per bersaglio, e inventare
 * una ripartizione sarebbe peggio che non farla.
 */
export const applyRoundsToTargets = (targets, rounds) => {
	const list = Array.isArray(targets) ? targets : [];
	const total = Number(rounds);
	if (!Number.isFinite(total) || total <= 0) return null;
	const scoring = list.filter((t) => defaultHitsRequired(t.target_type) > 0);
	const units = scoring.reduce((sum, t) => sum + (Number(t.count) || 1), 0);
	if (!units || total % units !== 0) return null;
	const each = total / units;
	return list.map((t) =>
		defaultHitsRequired(t.target_type) > 0 ? { ...t, hits_required: each } : t,
	);
};

/**
 * Etichetta `T1`, `T2-T4`, `NS` per una riga della tabella bersagli.
 *
 * È il filo che lega la tabella del coach alla scheda del tiratore: senza il
 * numero in tabella, «inserimento score su ogni sagoma» era una frase e non
 * una cosa che si vede.
 */
export const targetRowLabels = (targets) => {
	const list = Array.isArray(targets) ? targets : [];
	let next = 0;
	return list.map((target) => {
		if (defaultHitsRequired(target.target_type) === 0) return "NS";
		const count = Math.max(1, Number(target.count) || 1);
		const first = next + 1;
		next += count;
		return count === 1 ? `T${first}` : `T${first}-T${next}`;
	});
};

// ── Validazione del form ───────────────────────────────────────────────────

/**
 * Cosa manca perché l'esercizio si possa salvare.
 *
 * Il bottone disabilitato senza una parola era il difetto: il coach cliccava
 * e non succedeva niente, e non sapeva che mancava il nome. Qui si elencano
 * le mancanze, e il form le scrive accanto al bottone.
 */
export const shootingMissingFields = (exercise) => {
	const missing = [];
	if (!String(exercise?.exercise_name || "").trim()) missing.push("name");
	if (isShootingExerciseIncomplete(exercise)) missing.push("scoring_system");
	return missing;
};

// ── Risultato ──────────────────────────────────────────────────────────────

const positive = (counts) =>
	Object.values(counts || {}).some((n) => Number(n) > 0);

/** Una serie registrata senza tempo né impatti: una scheda lasciata vuota. */
export const isSeriesEmpty = (serie) => {
	if (!serie) return true;
	if (String(serie.time_seconds ?? "").trim() !== "") return false;
	if (positive(serie.hits) || positive(serie.penalties)) return false;
	return !(serie.targets || []).some(
		(target) => positive(target?.hits) || positive(target?.penalties),
	);
};

/**
 * Toglie le schede vuote e rinumera.
 *
 * Le schede nascono già pronte quando il coach ha previsto le serie; se il
 * tiratore ne spara tre su cinque, le due vuote non devono entrare nella
 * media come due zeri.
 */
export const pruneEmptySeries = (series) =>
	(Array.isArray(series) ? series : [])
		.filter((serie) => !isSeriesEmpty(serie))
		.map((serie, index) => ({ ...serie, series_index: index + 1 }));

/**
 * Il tempo serve solo quando entra nel punteggio.
 *
 * Con l'hit factor e il time-plus il cronometro è il dato; su un sistema a
 * soli punti (precisione, hit/miss) chiedere il tempo è una casella in più
 * da ignorare, e al poligono una casella in più è una in più da sbagliare.
 */
export const metricNeedsTime = (metric) =>
	String(metric || "hit_factor").toLowerCase() !== "points";

// ── Tempo ──────────────────────────────────────────────────────────────────

/**
 * Da ciò che si digita a secondi: `4,82` e `4.82` valgono uguale.
 *
 * La tastiera italiana propone la virgola, e un `type="number"` la rifiuta
 * in silenzio: era una delle ragioni per cui «non riesco a mettere due
 * cifre». `null` quando non c'è un numero.
 */
export const parseShotTime = (raw) => {
	const text = String(raw ?? "")
		.trim()
		.replace(",", ".")
		.replace(/[^\d.]/g, "");
	if (!text || text === ".") return null;
	const parsed = Number(text);
	if (!Number.isFinite(parsed) || parsed < 0) return null;
	return Math.round(parsed * 100) / 100;
};

/** Secondi come li legge un cronometro: due decimali, sempre. */
export const formatShotTime = (value) => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed < 0) return "";
	return parsed.toFixed(2);
};
